<template>
  <el-dialog title="Rate Matrix" :visible.sync="visible" :close-on-click-modal="false" width="1200px" top="2vh">
    <div class="mb10 contentHead">
      <span>Month:</span>
      <template v-if="dataRow.month">
        {{ dataRow.month.slice(0, 4) + "-" + dataRow.month.slice(4) }}
      </template>
    </div>
    <div class="pb16">
      <el-table tooltip-effect="dark" size="mini" fit :data="dataList" class="wdb100" height="80vh">
        <el-table-column label="Currency" header-align="center" align="left" width="130" fixed="left" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.currency }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Code" align="center" width="60" fixed="left">
          <template slot-scope="scope">
            <span>{{ scope.row.code }}</span>
          </template>
        </el-table-column>
        <el-table-column
          v-for="item in exRateCurrencyExclusiveList"
          :key="`rate-matrix-${item.currency}`"
          :label="item.currency"
          align="center"
          min-width="94"
        >
          <template slot-scope="scope">
            <span :class="{ bgGray: Number(scope.row[item.currency]) == 1 }">{{ scope.row[item.currency] }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </el-dialog>
</template>

<script>
import { isNumberLike } from "@/utils";
import { divide, multiply, fmoney, toFixedDecimal } from "@/utils/count";
export default {
  props: {
    exRateCurrencyExclusiveList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      visible: false,
      dataList: [],
      dataRow: {},
    };
  },
  methods: {
    // 初始化
    init(data) {
      this.dataList = [];
      const detailList = data.detailList || [];
      // SGD 为基准货币，接口不返回其汇率，手动插入一条 1:1 的记录
      if (!detailList.some(ele => ele.currency === "SGD")) {
        detailList.unshift({ currency: "SGD", exchangeRateBasis: "1", exchangeRateValue: "1" });
      }
      const exchangeRateObj = {};
      detailList.forEach(item => {
        exchangeRateObj[item.currency] = { exchangeRateBasis: item.exchangeRateBasis, exchangeRateValue: item.exchangeRateValue };
      });
      this.dataRow = {
        month: data.month,
        exchangeRateObj,
      };
      this.generateRateMatrix();
      this.visible = true;
    },
    // 生成汇率矩阵
    generateRateMatrix() {
      const matrix = [];
      // 小数位数规则：源货币属于6位小数币种且目标货币为VND时保留6位，否则保留4位
      const fromCurrency6DecimalPlaces = ["SGD", "EUR", "GBP", "USD", "SAR", "CHF", "THB", "AED"]; // 需要保留6位小数的币种
      const getDecimalPlaces = (fromCurrency, toCurrency) => {
        return fromCurrency6DecimalPlaces.includes(fromCurrency.currency) && toCurrency.currency == "VND" ? 6 : 4;
      };
      // 第一行、第一列先取整后的汇率值缓存，供其他区域交叉计算使用（与Excel引用ROUND后的单元格一致）
      const firstRowRateMap = {}; // 列货币 -> 第一行取整后的汇率值
      const firstColRateMap = {}; // 行货币 -> 第一列取整后的汇率值
      // 遍历所有货币，生成矩阵行
      this.exRateCurrencyExclusiveList.forEach((fromCurrency, fromIndex) => {
        const row = {
          currency: `${fromCurrency.currency} (${fromCurrency.title})`,
          code: fromCurrency.currency,
        };
        // 计算该行货币对其他所有货币的汇率
        this.exRateCurrencyExclusiveList.forEach((toCurrency, toIndex) => {
          const exchangeRateObj = this.dataRow.exchangeRateObj;
          const fromExRateBasis = exchangeRateObj[fromCurrency.currency]?.exchangeRateBasis;
          const fromExRateValue = exchangeRateObj[fromCurrency.currency]?.exchangeRateValue;
          const toExRateBasis = exchangeRateObj[toCurrency.currency]?.exchangeRateBasis;
          const toExRateValue = exchangeRateObj[toCurrency.currency]?.exchangeRateValue;
          // 计算汇率比例
          if (isNumberLike(fromExRateBasis) && isNumberLike(fromExRateValue) && isNumberLike(toExRateBasis) && isNumberLike(toExRateValue)) {
            const places = getDecimalPlaces(fromCurrency, toCurrency);
            let rate = 0;
            if (fromCurrency.currency === toCurrency.currency) {
              // 币种相同直接为 1
              rate = 1;
            } else if (fromIndex == 0) {
              // 第一行：ROUND(D34/D33,4)，目标货币单位汇率，先取整再缓存
              rate = Number(toFixedDecimal(divide(toExRateValue, toExRateBasis), places));
              firstRowRateMap[toCurrency.currency] = rate;
            } else if (toIndex == 0) {
              // 第一列：ROUND(1/(G34/G33),4)，源货币单位汇率的倒数，先取整再缓存
              rate = Number(toFixedDecimal(divide(1, divide(fromExRateValue, fromExRateBasis)), places));
              firstColRateMap[fromCurrency.currency] = rate;
            } else {
              // 其他区域：ROUND(D$6*$C8,4)，行值(第一列取整后) * 列值(第一行取整后)
              rate = multiply(firstColRateMap[fromCurrency.currency], firstRowRateMap[toCurrency.currency]);
            }
            // 显示值 = 参与计算的取整值，与Excel单元格行为一致
            row[toCurrency.currency] = fmoney(rate, places);
          } else {
            row[toCurrency.currency] = "N/A";
          }
        });
        matrix.push(row);
      });
      this.dataList = matrix;
    },
  },
};
</script>

<style lang="scss" scoped>
.contentHead {
  width: 100%;
  height: 40px;
  line-height: 40px;
  padding: 0 14px;
  background: #f5f7fa;
  font-size: 14px;
  color: #606266;

  span {
    color: #909399;
  }
}

.bgGray {
  display: inline-block;
  width: 100%;
  height: 100%;
  background: #ebeef5;
}
</style>
